import { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtMoney, fmtDate } from "../lib/format";
import PlanFromSelectionPanel from "../components/PlanFromSelectionPanel";
import HomeBaseModal from "../components/HomeBaseModal";
import {
  Plane, Train, Hotel as HotelIcon, Calendar as CalendarIcon, MapPin,
  Sparkles, ChevronDown, ChevronRight, ChevronLeft, AlertCircle,
  CheckCircle2, Download, Save, Trophy, Database, Brain, Route,
  Bell, BellRing, ClipboardList, Smartphone, ArrowRight, Tv, Filter,
  Check, ExternalLink, Loader2,
} from "lucide-react";

const BADGE_META = {
  must_visit:  { label: "Must visit",   tone: "pm-pill-red",   color: "#dc2626" },
  high:        { label: "High",         tone: "pm-pill-blue",  color: "#2563eb" },
  recommended: { label: "Recommended",  tone: "pm-pill-green", color: "#059669" },
  monitor:     { label: "Monitor",      tone: "pm-pill-slate", color: "#64748b" },
  travel:      { label: "Travel day",   tone: "pm-pill-amber", color: "#d97706" },
};

const STEPS = [
  { n: "01", icon: Database, title: "Pull all fixtures",   body: "Every client's match schedule synced from their league calendar." },
  { n: "02", icon: Brain,    title: "AI scores visits",    body: "Each match rated by urgency, client importance, and visit recency." },
  { n: "03", icon: Route,    title: "Cluster into trips",  body: "Nearby fixtures on same dates grouped into multi-match trip routes." },
  { n: "04", icon: Plane,    title: "Flight + hotel",      body: "Scrape flight options, generate full itinerary with times and costs." },
];

const FEATURES = [
  { title: "Fixture feed",         icon: CalendarIcon,  body: "Auto-synced from league APIs, Wyscout, or Transfermarkt. Updates on rescheduled or postponed fixtures." },
  { title: "AI trip builder",      icon: Brain,         body: "Generates 3 alternative trip options per planning window, ranked by AI score. Regenerate with constraints (max 3 days, €500 budget)." },
  { title: "Flight scraping",      icon: Plane,         body: "Scrapes Google Flights, Skyscanner, or airline direct APIs. Shows cheapest, fastest, and best-value options per leg." },
  { title: "Hotel suggestions",    icon: HotelIcon,     body: "Suggests hotels near each match venue within the agent's preferred price range. Booking.com or similar API." },
  { title: "Stadium logistics",    icon: MapPin,        body: "Stadium name, address, capacity, accreditation contact, and public transport options auto-attached. Accreditation request template pre-filled." },
  { title: "Itinerary export",     icon: Download,      body: "Full trip itinerary exported as PDF or shared as a read-only link. Includes all flight details, match times, stadium addresses, hotel confirmations." },
  { title: "Calendar sync",        icon: CalendarIcon,  body: "One-click push of the full itinerary to Google Calendar or Outlook. Each stop becomes a separate event with all details in the description." },
  { title: "Mobile trip view",     icon: Smartphone,    body: "Full itinerary available offline on mobile. Each stop shows boarding pass link, stadium map, and a quick log-visit button." },
  { title: "Live disruption alerts", icon: Bell,        body: "Flight delays, postponed matches, or fixture time changes trigger immediate push alerts with re-planning options." },
  { title: "Visit log",            icon: ClipboardList, body: "After each attended match, agent logs: attended, notes, conversation summary, next steps. Stored on the client record and counted in recency tracking." },
];

const INTEGRATIONS = [
  "League fixture APIs", "Wyscout fixture data", "Transfermarkt fixture data",
  "Google Flights / Skyscanner API", "Rail: Trainline / SNCB / NS API",
  "Booking.com / Hotels API", "Google Calendar", "Outlook Calendar",
  "Google Maps (stadium routing)", "LLM (AI trip scoring & clustering)",
];

const STOP_ICON = {
  travel: Plane, match: Trophy, hotel: HotelIcon,
};

export default function TravelPlanner() {
  const [tab, setTab] = useState("schedule"); // "schedule" | "travel"
  const [overview, setOverview] = useState(null);
  const [weekStart, setWeekStart] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [suggest, setSuggest] = useState(null);
  const [logic, setLogic] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [savedTrips, setSavedTrips] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeOption, setActiveOption] = useState(0);
  const [openLogic, setOpenLogic] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  // schedule-tab filters
  const [filterClient, setFilterClient] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  // Persist the "All upcoming fixtures" fold state — the list can be very long.
  const [upcomingCollapsed, setUpcomingCollapsed] = useState(() => {
    try { return localStorage.getItem("travel_upcoming_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("travel_upcoming_collapsed", upcomingCollapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [upcomingCollapsed]);
  // Trip-planning selection state
  const [selectedFids, setSelectedFids] = useState(() => new Set());
  const [planResult, setPlanResult] = useState(null);     // {options:[...], ai_used}
  const [planning, setPlanning] = useState(false);
  const [activePlanIdx, setActivePlanIdx] = useState(0);
  const [enrichments, setEnrichments] = useState({});      // {legKey: {kind, status, data}}
  const [savedItins, setSavedItins] = useState([]);

  const toggleSelect = (id) => setSelectedFids((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Home base — agent's saved travel origin (city + airport IATA)
  const [homeBase, setHomeBase] = useState(null); // {home_city, home_iata, airport}
  const [homeBaseModalOpen, setHomeBaseModalOpen] = useState(false);

  // Load saved home base once
  useEffect(() => {
    api.get("/travel/home-base").then(({ data }) => {
      if (data?.home_iata) setHomeBase({ home_city: data.home_city, home_iata: data.home_iata, airport: data.airport });
    }).catch(() => {});
  }, []);

  const runPlanWithCity = async (originCity, originIata) => {
    setPlanning(true);
    try {
      const { data } = await api.post("/travel-planner/plan", {
        fixture_ids: Array.from(selectedFids),
        home_city: originCity,
        home_iata: originIata || undefined,
        max_days: 7,
        max_budget_eur: 1500,
      });
      setPlanResult(data);
      setActivePlanIdx(0);
      setTab("travel");
      setTimeout(() => {
        const el = document.querySelector('[data-testid="plan-from-selection-panel"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (e) {
      flashToast("Could not plan trip — try again");
    } finally {
      setPlanning(false);
    }
  };

  const planFromSelection = async () => {
    if (!selectedFids.size) return;
    // Gate: ask the agent where they're travelling from on first use
    if (!homeBase?.home_city) {
      setHomeBaseModalOpen(true);
      return;
    }
    await runPlanWithCity(homeBase.home_city, homeBase.home_iata);
  };

  const onHomeBaseConfirmed = async ({ home_city, home_iata, airport }) => {
    setHomeBase({ home_city, home_iata, airport });
    setHomeBaseModalOpen(false);
    // Continue the planning the user originally triggered
    if (selectedFids.size) {
      await runPlanWithCity(home_city, home_iata);
    }
  };

  const clearPlan = () => {
    setPlanResult(null);
    setSelectedFids(new Set());
  };

  const loadSavedItineraries = useCallback(async () => {
    try {
      const r = await api.get("/travel-planner/itineraries");
      setSavedItins(r.data?.itineraries || []);
    } catch (e) { /* graceful */ }
  }, []);

  const loadOverview = useCallback(async (startISO) => {
    const url = startISO
      ? `/travel-planner/weekly-overview?week_start=${startISO}`
      : "/travel-planner/weekly-overview";
    const r = await api.get(url);
    setOverview(r.data);
    setWeekStart(r.data.week_start);
  }, []);

  const loadAll = async () => {
    const [a, b, c, d, e, f, g] = await Promise.all([
      api.get("/travel-planner/weekly-overview"),
      api.get("/travel-planner/fixtures"),
      api.get("/travel-planner/trips/suggest"),
      api.get("/travel-planner/scoring-logic"),
      api.get("/travel-planner/clients-overdue"),
      api.get("/travel-planner/trips"),
      api.get("/travel-planner/match-schedule-stats"),
    ]);
    setOverview(a.data);
    setWeekStart(a.data.week_start);
    setFixtures(b.data || []);
    setSuggest(c.data);
    setLogic(d.data || []);
    setOverdue(e.data || []);
    setSavedTrips(f.data || []);
    setStats(g.data);
  };

  useEffect(() => { loadAll(); }, []);

  // Scroll to #overdue when arriving from dashboard via hash deep-link
  useEffect(() => {
    if (!overdue.length) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#overdue") return;
    const t = setTimeout(() => {
      const el = document.getElementById("overdue");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(t);
  }, [overdue]);

  const flashToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const activeTrip = useMemo(() => suggest?.options?.[activeOption] || null, [suggest, activeOption]);

  // Schedule-tab fixture filters
  const filteredFixtures = useMemo(() => {
    return fixtures.filter((fx) => {
      if (filterClient && fx.client.id !== filterClient) return false;
      if (filterPriority && fx.badge !== filterPriority) return false;
      return true;
    }).sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff));
  }, [fixtures, filterClient, filterPriority]);

  // Distinct client names across currently-selected fixtures — shown in the tray.
  const selectedClientNames = useMemo(() => {
    const names = [];
    const seen = new Set();
    for (const fx of fixtures) {
      if (!selectedFids.has(fx.id)) continue;
      const n = fx.client?.full_name;
      if (n && !seen.has(n)) { seen.add(n); names.push(n); }
    }
    return names;
  }, [fixtures, selectedFids]);

  const saveItinerary = async () => {
    if (!activeTrip) return;
    setBusy(true);
    try {
      const payload = { ...activeTrip, excluded_clients: suggest?.excluded || [] };
      await api.post("/travel-planner/trips", payload);
      const { data } = await api.get("/travel-planner/trips");
      setSavedTrips(data || []);
      flashToast("Itinerary saved");
    } finally { setBusy(false); }
  };

  const calendarSync = async () => {
    if (!activeTrip) return;
    setBusy(true);
    try {
      const payload = { ...activeTrip, excluded_clients: suggest?.excluded || [] };
      const { data: saved } = await api.post("/travel-planner/trips", payload);
      // Build calendar events with full booking links in descriptions (enriched stops respected)
      let eventCount = 0;
      try {
        const { data: cal } = await api.post("/travel/calendar-events", activeTrip);
        eventCount = cal.events_count || 0;
      } catch { /* fallback to legacy sync */ }
      await api.post(`/travel-planner/trips/${saved.id}/calendar-sync`, { provider: "google" });
      const { data } = await api.get("/travel-planner/trips");
      setSavedTrips(data || []);
      flashToast(eventCount > 0
        ? `${eventCount} events pushed with booking links`
        : "Synced to Google Calendar (MOCKED)");
    } finally { setBusy(false); }
  };

  // TV reminder dialog state
  const [reminderFx, setReminderFx] = useState(null);
  const [reminderTiming, setReminderTiming] = useState("24h");
  const [reminderCal, setReminderCal] = useState("google");

  const openReminderPanel = (fx) => {
    setReminderFx(fx);
    setReminderTiming("24h");
    setReminderCal("google");
  };

  // Confirm TV reminder from the dialog: persist the flag + the chosen options
  const confirmTvReminder = async () => {
    if (!reminderFx) return;
    const fx = reminderFx;
    try {
      await api.post(`/travel-planner/fixtures/${fx.id}/tv-reminder`, {
        on: true,
        timing: reminderTiming,
        calendar: reminderCal,
      });
      setFixtures((all) => all.map((f) => (f.id === fx.id ? { ...f, tv_reminder: true, tv_reminder_timing: reminderTiming, tv_reminder_calendar: reminderCal } : f)));
      api.get("/travel-planner/match-schedule-stats").then((r) => setStats(r.data));
      setOverview((ov) => {
        if (!ov) return ov;
        return {
          ...ov,
          days: ov.days.map((d) => ({
            ...d,
            fixtures: d.fixtures.map((f) => (f.id === fx.id ? { ...f, tv_reminder: true } : f)),
          })),
        };
      });
      flashToast("TV reminder set");
    } catch {
      flashToast("Could not set reminder");
    } finally {
      setReminderFx(null);
    }
  };

  // Toggle reminder off — used when fx already has a reminder set and user clicks the chip
  const removeTvReminder = async (fx) => {
    try {
      await api.post(`/travel-planner/fixtures/${fx.id}/tv-reminder`, { on: false });
      setFixtures((all) => all.map((f) => (f.id === fx.id ? { ...f, tv_reminder: false } : f)));
      api.get("/travel-planner/match-schedule-stats").then((r) => setStats(r.data));
      setOverview((ov) => {
        if (!ov) return ov;
        return {
          ...ov,
          days: ov.days.map((d) => ({
            ...d,
            fixtures: d.fixtures.map((f) => (f.id === fx.id ? { ...f, tv_reminder: false } : f)),
          })),
        };
      });
      flashToast("TV reminder removed");
    } catch {
      flashToast("Could not update reminder");
    }
  };

  // Deep-link / cross-tab scroll: when tab flips to "travel" with a pending target, scroll once mounted.
  const [pendingScroll, setPendingScroll] = useState(null);
  useEffect(() => {
    if (tab !== "travel" || !pendingScroll) return;
    const el = document.querySelector(`[data-testid="${pendingScroll}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setPendingScroll(null);
  }, [tab, pendingScroll]);

  const goPlanVisit = () => { setPendingScroll("suggested-trip-section"); setTab("travel"); };

  // Week navigation for the Match Schedule tab grid
  const shiftWeek = async (deltaDays) => {
    if (!weekStart) return;
    const d = new Date(weekStart);
    d.setUTCDate(d.getUTCDate() + deltaDays);
    const startISO = d.toISOString().slice(0, 10);
    await loadOverview(startISO);
  };

  if (!overview || !suggest) {
    return (
      <Layout title="Match schedule & travel planner" subtitle="Module 10 · AI-powered field visits">
        <div className="text-slate-500" data-testid="travel-planner-loading">Loading fixtures and trip options…</div>
      </Layout>
    );
  }

  // Empty state: trip generation failed OR no scored fixtures available
  if (suggest.state === "generation_failed" || suggest.state === "no_upcoming_fixtures" ||
      (!suggest.options?.length && !suggest.all_scored?.length)) {
    return (
      <Layout title="Match schedule & travel planner" subtitle="Module 10 · AI-powered field visits">
        <div className="pm-card p-10 text-center" data-testid="travel-planner-empty-state">
          <Plane className="mx-auto mb-3 text-slate-400" size={48} />
          <h2 className="font-display text-2xl font-black tracking-tight mb-2">
            {suggest.state === "generation_failed" ? "Could not generate trip suggestions" : "No fixtures to plan around yet"}
          </h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto mb-4">
            {suggest.message || "Fixture data will appear here once your clients' clubs are linked to the fixture database. This usually happens automatically within a few minutes of adding a new client."}
          </p>
          <a href="/settings/integrations" className="pm-btn-primary inline-flex items-center gap-1.5 h-9 px-4 rounded-xl text-xs font-bold" data-testid="travel-empty-cta">
            Open Integrations →
          </a>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Match schedule & travel planner"
      subtitle="Module 10 · AI-powered field visits"
      right={
        <div className="hidden md:flex items-center gap-2">
          <span className="pm-pill pm-pill-amber text-[10px]" data-testid="travel-mocked-pill">MOCKED</span>
        </div>
      }
    >
      <div className="space-y-8" data-testid="travel-planner-root">
        {/* TAB STRIP */}
        <div className="border-b border-slate-200 -mt-2" data-testid="travel-tabs">
          <div className="flex items-center gap-1">
            <TabButton active={tab === "schedule"} onClick={() => setTab("schedule")} icon={CalendarIcon} label="Match schedule" testid="tab-schedule" />
            <TabButton active={tab === "travel"} onClick={() => setTab("travel")} icon={Plane} label="Travel planner" testid="tab-travel" />
          </div>
        </div>

        {/* === MATCH SCHEDULE TAB === */}
        {tab === "schedule" && (
          <div className="space-y-8" data-testid="match-schedule-panel">
            {/* KPI strip */}
            <section data-testid="match-kpis" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard
                testid="kpi-fixtures-this-month"
                label="Fixtures this month"
                value={stats?.fixtures_this_month ?? "—"}
                sub={stats ? `Across ${stats.clients_with_fixtures} clients` : "Loading"}
                tone="slate"
              />
              <KpiCard
                testid="kpi-tv-reminders"
                label="TV reminders set"
                value={stats?.tv_reminders_set ?? "—"}
                sub="Calendar notifications"
                tone="blue"
              />
              <KpiCard
                testid="kpi-must-visit"
                label="Must visit"
                value={stats?.must_visit ?? "—"}
                sub="Require travel"
                tone="red"
              />
              <KpiCard
                testid="kpi-clients-overdue"
                label="Clients not visited"
                value={stats?.clients_overdue_45d ?? "—"}
                sub="45+ days overdue"
                tone="amber"
              />
            </section>

            {/* Smaller-client guarantee banner */}
            {overdue.length > 0 && (
              <div
                className="pm-card p-4 flex items-center gap-4 border-l-4 border-l-amber-400 bg-amber-50/40"
                data-testid="overdue-banner"
              >
                <AlertCircle size={18} className="text-amber-700 shrink-0" />
                <div className="text-sm text-slate-800 flex-1 min-w-0">
                  <span className="font-bold">{overdue.length} client{overdue.length === 1 ? "" : "s"} not visited in 45+ days:</span>{" "}
                  {overdue.slice(0, 4).map((c, i) => (
                    <span key={c.id}>
                      <span className="font-semibold">{c.full_name}</span>
                      <span className="text-slate-500"> ({c.days_since_last_visit != null ? `${c.days_since_last_visit}d` : "never"}){i < Math.min(overdue.length - 1, 3) ? ", " : ""}</span>
                    </span>
                  ))}
                  {overdue.length > 4 && <span className="text-slate-500"> and {overdue.length - 4} more</span>}.
                  <span className="text-slate-600"> Their next fixtures are flagged below.</span>
                </div>
                <button
                  onClick={goPlanVisit}
                  className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-2 shrink-0"
                  data-testid="overdue-plan-visit-btn"
                >
                  Plan visit <ArrowRight size={14} />
                </button>
              </div>
            )}

            {/* Week-of grid */}
            <section data-testid="week-of-section">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Match schedule</div>
                  <h2 className="font-display text-xl font-black tracking-tight">
                    Week of {fmtDateShort(overview.week_start)} – {fmtDateShort(overview.week_end)}
                  </h2>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={() => shiftWeek(-7)} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="week-prev">
                    <ChevronLeft size={12} /> Prev week
                  </button>
                  <button onClick={() => loadOverview()} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="week-today">
                    This week
                  </button>
                  <button onClick={() => shiftWeek(7)} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="week-next">
                    Next week <ChevronRight size={12} />
                  </button>
                </div>
              </div>
              <Legend />
              <p className="text-[11px] text-slate-500 mt-1.5 max-w-2xl" data-testid="week-select-hint">
                <Sparkles size={10} className="inline mr-1 text-emerald-600" />
                <b>Click any match below</b> to select it for trip planning. Selected matches appear in the green action bar at the bottom — click <b>Plan trip</b> to generate an AI itinerary.
              </p>
              <div className="pm-card overflow-x-auto pm-scroll mt-2">
                <div className="min-w-[900px] grid grid-cols-7 gap-px bg-slate-200">
                  {overview.days.map((day) => {
                    const dt = new Date(day.date);
                    const dayName = dt.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase();
                    const dayNum = dt.getDate();
                    const today = dt.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10);
                    return (
                      <div key={day.date} className={`bg-white p-3 min-h-[160px] ${today ? "ring-1 ring-emerald-400 ring-inset" : ""}`} data-testid={`week-day-${day.date}`}>
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">{dayName} {dayNum}</div>
                        <div className="mt-2 space-y-1.5">
                          {day.fixtures.length === 0 && <div className="text-[11px] text-slate-300">—</div>}
                          {day.fixtures.map((fx) => {
                            const b = BADGE_META[fx.badge];
                            const tone = fx.tv_reminder ? "#7c3aed" : b.color;
                            const isSelected = selectedFids.has(fx.id);
                            return (
                              <button
                                type="button"
                                key={fx.id}
                                onClick={() => toggleSelect(fx.id)}
                                className={`w-full text-left rounded-lg px-2 py-1.5 border text-[11px] flex items-start gap-1.5 transition-all ${isSelected ? "ring-2 ring-emerald-500 ring-offset-1" : "hover:border-slate-400"}`}
                                style={{ borderColor: tone + "55", background: isSelected ? "#10b98122" : tone + "14" }}
                                data-testid={`week-fx-${fx.id}`}
                                aria-pressed={isSelected}
                                title={isSelected ? "Click to unselect" : "Click to select for trip planning"}
                              >
                                {isSelected ? (
                                  <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[8px] mt-px shrink-0 font-black">✓</span>
                                ) : (
                                  <span className="w-2.5 h-2.5 rounded-full mt-0.5 shrink-0 ring-1 ring-white/60" style={{ background: tone }} />
                                )}
                                <span className="flex-1 min-w-0 block">
                                  <span className="font-bold text-slate-900 truncate block" data-testid={`week-fx-checkbox-${fx.id}`}>{shortName(fx.client.full_name)}</span>
                                  <span className="text-slate-600 truncate block">{fx.competition}</span>
                                  <span className="font-mono-tab text-[10px] text-slate-500 block">{fx.kickoff.slice(11, 16)}</span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* All upcoming fixtures + filters + per-row actions */}
            <section data-testid="upcoming-fixtures-section">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setUpcomingCollapsed((v) => !v)}
                  data-testid="upcoming-fixtures-toggle"
                  aria-expanded={!upcomingCollapsed}
                  className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
                >
                  <ChevronDown
                    size={14}
                    className="transition-transform duration-200 text-slate-500"
                    style={{ transform: upcomingCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }}
                  />
                  <SectionTitle>
                    <span className="flex items-center gap-2">
                      <Filter size={14} /> All upcoming fixtures
                      <span className="text-xs font-bold text-slate-500 font-mono-tab">({filteredFixtures.length})</span>
                    </span>
                  </SectionTitle>
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <select
                    className="pm-input text-xs h-9"
                    value={filterClient}
                    onChange={(e) => setFilterClient(e.target.value)}
                    data-testid="filter-client"
                  >
                    <option value="">All clients</option>
                    {Array.from(new Map(fixtures.map((f) => [f.client.id, f.client.full_name])).entries()).map(([cid, name]) => (
                      <option key={cid} value={cid}>{name}</option>
                    ))}
                  </select>
                  <select
                    className="pm-input text-xs h-9"
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    data-testid="filter-priority"
                  >
                    <option value="">All priority</option>
                    <option value="must_visit">Must visit</option>
                    <option value="high">High</option>
                    <option value="recommended">Recommended</option>
                    <option value="monitor">Monitor</option>
                  </select>
                </div>
              </div>
              {!upcomingCollapsed && (
              <div className="pm-card divide-y divide-slate-100" data-testid="upcoming-list">
                {filteredFixtures.length === 0 && (
                  <div className="p-8 text-center text-sm text-slate-500" data-testid="upcoming-empty">No fixtures match this filter.</div>
                )}
                {filteredFixtures.map((fx) => {
                  const b = BADGE_META[fx.badge];
                  const isFlagged = fx.days_since_last_visit != null && fx.days_since_last_visit >= 45;
                  return (
                    <div key={fx.id} className={`p-4 flex items-center gap-3 ${isFlagged ? "bg-amber-50/30" : ""} ${selectedFids.has(fx.id) ? "bg-emerald-50/40" : ""}`} data-testid={`fixture-row-${fx.id}`}>
                      <input
                        type="checkbox"
                        checked={selectedFids.has(fx.id)}
                        onChange={() => toggleSelect(fx.id)}
                        className="w-4 h-4 accent-emerald-600 cursor-pointer shrink-0"
                        data-testid={`fixture-checkbox-${fx.id}`}
                        aria-label={`Select ${fx.client.full_name} match`}
                      />
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: b.color }} />
                      <div className="w-44 shrink-0 min-w-0">
                        <div className="font-bold text-slate-900 truncate flex items-center gap-2">
                          {fx.client.full_name}
                          <span className="text-[10px] font-mono-tab text-slate-400">T{fx.tier}</span>
                        </div>
                        {isFlagged && <div className="text-[10px] text-amber-700 font-bold" data-testid={`flagged-${fx.id}`}>Not visited {fx.days_since_last_visit} days — flagged</div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900 truncate">{fx.home_team} vs {fx.away_team}</div>
                        <div className="text-[11px] text-slate-500 truncate">{fx.competition}</div>
                      </div>
                      <div className="font-mono-tab text-xs text-slate-700 w-28 text-right shrink-0">
                        {new Date(fx.kickoff).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}
                        <div className="text-slate-500">{fx.kickoff.slice(11, 16)}</div>
                      </div>
                      <button
                        onClick={() => (fx.tv_reminder ? removeTvReminder(fx) : openReminderPanel(fx))}
                        className={`h-9 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1.5 border transition-all shrink-0 ${fx.tv_reminder ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                        data-testid={`tv-reminder-${fx.id}`}
                        title={fx.tv_reminder ? "Click to remove" : "Set TV-watching reminder"}
                      >
                        {fx.tv_reminder ? <BellRing size={11} /> : <Tv size={11} />}
                        {fx.tv_reminder ? "Reminder set" : "Watch on TV"}
                      </button>
                      <button
                        onClick={goPlanVisit}
                        disabled={fx.badge === "monitor" && !isFlagged}
                        className="pm-btn-secondary h-9 px-3 rounded-lg text-[11px] font-bold flex items-center gap-1.5 shrink-0 disabled:opacity-40"
                        data-testid={`travel-cta-${fx.id}`}
                        title={fx.badge === "monitor" && !isFlagged ? "Too far for a visit — keep on TV" : "Plan a travel itinerary"}
                      >
                        {fx.badge === "monitor" && !isFlagged ? "Too far" : (<><Route size={11} /> Travel</>)}
                      </button>
                    </div>
                  );
                })}
              </div>
              )}
            </section>

            {/* Selection tray — fixed to viewport bottom, slides up/down with CSS transition.
                Stays visible while scrolling; never escapes the screen.
                Renders OUTSIDE the schedule panel so it's always mounted (avoids unmount/remount glitches). */}
          </div>
        )}

        {/* ====== Sticky selection tray (rendered via portal so position:fixed isn't broken
             by ancestor transforms from the page fade-in animation). ====== */}
        {createPortal(
          <div
            className={`selection-tray ${selectedFids.size > 0 ? "visible" : ""}`}
            data-testid="plan-trip-bar"
            role="region"
            aria-label="Trip planning tray"
            aria-hidden={selectedFids.size === 0}
          >
            <div className="selection-tray-inner">
              <div className="selection-tray-summary">
                <span className="selection-tray-count" data-testid="plan-trip-count">
                  <Trophy size={14} className="text-emerald-400" />
                  {selectedFids.size} {selectedFids.size === 1 ? "match" : "matches"} selected
                </span>
                {selectedClientNames.length > 0 && (
                  <span className="selection-tray-clients">
                    <span className="hidden sm:inline">·</span>
                    {selectedClientNames.slice(0, 3).map((n, i) => (
                      <span key={n} className="selection-tray-client">
                        {n}{i < Math.min(selectedClientNames.length, 3) - 1 ? "," : ""}
                      </span>
                    ))}
                    {selectedClientNames.length > 3 && (
                      <span className="selection-tray-client">
                        + {selectedClientNames.length - 3} more
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="selection-tray-actions">
                <button
                  onClick={() => setHomeBaseModalOpen(true)}
                  data-testid="plan-trip-home-base"
                  className="selection-tray-home"
                  title="Change starting airport"
                >
                  <MapPin size={11} className="text-emerald-400" />
                  From: <span className="selection-tray-home-city">
                    {homeBase?.airport ? `${homeBase.airport.city} (${homeBase.airport.iata})`
                     : homeBase?.home_city ? `${homeBase.home_city}${homeBase.home_iata ? ` (${homeBase.home_iata})` : ""}`
                     : "Set airport"}
                  </span>
                  <span className="text-white/40 text-[10px]">✎</span>
                </button>
                <button
                  onClick={() => setSelectedFids(new Set())}
                  data-testid="plan-trip-clear"
                  className="selection-tray-clear"
                >Clear</button>
                <button
                  onClick={planFromSelection}
                  disabled={planning}
                  data-testid="plan-trip-go"
                  className="selection-tray-go"
                >
                  {planning ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {planning ? "Planning…" : "Plan trip"}
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
        {tab === "travel" && (
          <div className="space-y-8" data-testid="travel-panel">
        {/* NEW: Plan from selection (Layer 1 + Layer 2 + Layer 3) */}
        {planResult && (
          <PlanFromSelectionPanel
            planResult={planResult}
            onClear={clearPlan}
            onToast={flashToast}
          />
        )}
        {/* HOW IT WORKS */}
        <section data-testid="how-it-works">
          <SectionTitle>How it works · from fixture to itinerary</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="pm-card p-4" data-testid={`how-step-${s.n}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-mono-tab text-xs text-slate-400">{s.n}</span>
                    <Icon size={14} className="text-emerald-700" />
                  </div>
                  <div className="font-display font-bold text-sm tracking-tight mb-1">{s.title}</div>
                  <div className="text-xs text-slate-500 leading-relaxed">{s.body}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* WEEKLY OVERVIEW moved to Match Schedule tab — see week-of-section */}

        {/* AI VISIT SCORING TABLE */}
        <section data-testid="visit-scoring-section">
          <SectionTitle>AI visit scoring · all upcoming fixtures</SectionTitle>
          <p className="text-xs text-slate-500 -mt-1 mb-3 max-w-3xl leading-relaxed" data-testid="visit-scoring-explainer">
            Score combines <b>client tier</b> · <b>contract proximity</b> · <b>visit recency</b> · <b>match importance</b> — then boosted when fixtures cluster geographically and forced up for the smaller-client guarantee (no client goes &gt; 60 days without a visit).
          </p>
          <div className="pm-card overflow-hidden">
            <div className="overflow-x-auto pm-scroll">
              <table className="w-full text-sm" data-testid="visit-scoring-table">
                <thead className="bg-slate-50">
                  <tr>
                    <Th>Client</Th>
                    <Th>Match</Th>
                    <Th>Date</Th>
                    <Th>Score</Th>
                    <Th>Badge</Th>
                  </tr>
                </thead>
                <tbody>
                  {fixtures.map((fx) => {
                    const b = BADGE_META[fx.badge];
                    return (
                      <tr key={fx.id} className="border-t border-slate-100" data-testid={`score-row-${fx.id}`}>
                        <Td>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ background: b.color }} />
                            <span className="font-bold text-slate-800 truncate">{fx.client.full_name}</span>
                            <span className="text-[10px] font-mono-tab text-slate-400">T{fx.tier}</span>
                          </div>
                        </Td>
                        <Td>
                          <div className="truncate">
                            <span className="font-semibold">{fx.home_team} vs {fx.away_team}</span>
                            <span className="text-slate-400"> · {fx.competition}</span>
                          </div>
                        </Td>
                        <Td>
                          <div className="font-mono-tab text-xs">{fx.kickoff.slice(0, 10)} <span className="text-slate-400">{fx.kickoff.slice(11, 16)}</span></div>
                        </Td>
                        <Td><span className="font-mono-tab font-bold">{fx.score}</span></Td>
                        <Td><span className={`pm-pill ${b.tone}`}>{b.label}</span></Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* AI SUGGESTED TRIP */}
        <section data-testid="suggested-trip-section" className="pt-3 mt-2 border-t border-slate-200">
          <SectionTitle>
            <span className="text-emerald-700">AI</span><span>&nbsp;suggested trip</span>
            <span className="text-slate-400 ml-2">· Option {activeOption + 1} of {suggest.options.length}</span>
            <div className="ml-auto flex items-center gap-1">
              {suggest.options.map((o, i) => (
                <button
                  key={i}
                  onClick={() => setActiveOption(i)}
                  className={`w-8 h-8 rounded-lg text-xs font-bold ${i === activeOption ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"}`}
                  data-testid={`option-tab-${i + 1}`}
                >{i + 1}</button>
              ))}
            </div>
          </SectionTitle>
          <p className="text-xs text-slate-500 -mt-1 mb-3 max-w-3xl leading-relaxed" data-testid="trip-score-explainer">
            <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-bold mr-1.5 align-middle">SCORE</span>
            Trips are built by clustering high-score fixtures geographically. Each match's score combines <b>client tier</b> · <b>contract proximity</b> · <b>visit recency</b> · <b>match importance</b>, then upgrades are applied for active legal cases, renewals in progress, and the smaller-client guarantee.
          </p>
          {activeTrip && (
            <TripCard
              trip={activeTrip}
              onSave={saveItinerary}
              onCalendarSync={calendarSync}
              busy={busy}
              activeIndex={activeOption}
            />
          )}
          {/* Excluded callout */}
          {suggest.excluded?.length > 0 && (
            <div className="mt-3 pm-card p-4 flex items-start gap-3 border-l-4 border-l-blue-400 bg-blue-50/40" data-testid="excluded-callout">
              <AlertCircle size={16} className="text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-700">
                {suggest.excluded.map((ex, i) => (
                  <div key={i} className="mb-1.5">
                    <b>{ex.client_name}</b> ({ex.fixture.home_team} vs {ex.fixture.away_team}, {fmtDate(ex.fixture.kickoff)})
                    {" — "}
                    {ex.reason}.{" "}
                    <button className="font-bold text-emerald-700 underline" data-testid={`excluded-show-${i}`}>
                      Show standalone {ex.client_name.split(" ")[0]} options
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* AI SCORING LOGIC */}
        <section data-testid="scoring-logic-section">
          <SectionTitle>AI scoring logic · how visits are ranked</SectionTitle>
          <div className="pm-card divide-y divide-slate-100">
            {logic.map((rule, i) => {
              const isOpen = openLogic === rule.id || (openLogic === null && i === 0);
              return (
                <div key={rule.id} className="p-4 border-l-4 border-l-blue-400">
                  <button
                    onClick={() => setOpenLogic(isOpen ? "_" : rule.id)}
                    className="w-full flex items-center justify-between text-left"
                    data-testid={`logic-toggle-${rule.id}`}
                  >
                    <div className="font-display font-bold text-base tracking-tight">{rule.title}</div>
                    {isOpen ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                  </button>
                  {isOpen && (
                    <div className="mt-2 text-sm text-slate-600 leading-relaxed" data-testid={`logic-body-${rule.id}`}>{rule.body}</div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* BALANCE COUNTER */}
        {overdue.length > 0 && (
          <section id="overdue" data-testid="overdue-section">
            <SectionTitle>
              <span className="flex items-center gap-2">
                <AlertCircle size={16} className="text-amber-600" />
                Smaller-client guarantee · {overdue.length} client{overdue.length !== 1 ? "s" : ""} not visited in 45+ days
              </span>
            </SectionTitle>
            <div className="pm-card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {overdue.map((c) => {
                const nextFx = fixtures.find((fx) => fx.client?.id === c.id);
                return (
                  <div key={c.id} className="border border-amber-200 bg-amber-50/40 rounded-xl p-3 flex items-center gap-3" data-testid={`overdue-${c.id}`}>
                    <div
                      className="w-10 h-10 rounded-full bg-slate-200 shrink-0"
                      style={{ backgroundImage: c.photo_url ? `url(${c.photo_url})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-sm truncate">{c.full_name}</div>
                      <div className="text-xs text-slate-500 truncate">{c.position} · {c.current_club}</div>
                      <div className="text-[11px] font-mono-tab text-amber-700 font-bold">{c.days_since_last_visit != null ? `${c.days_since_last_visit}d since last visit` : "Never visited"} · Tier {c.tier}</div>
                      {nextFx && (
                        <div className="text-[11px] text-slate-500 mt-0.5 truncate">Next: {nextFx.home_team} vs {nextFx.away_team} · {nextFx.kickoff.slice(0,10)}</div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        const el = document.querySelector('[data-testid="suggested-trip-section"]');
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      className="pm-btn-primary h-8 px-2.5 rounded-lg text-[11px] font-bold flex items-center gap-1 shrink-0"
                      data-testid={`overdue-plan-${c.id}`}
                    >
                      <Route size={11} /> Plan trip
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* FEATURE SPEC */}
        <section data-testid="feature-spec-section">
          <SectionTitle>Full feature specification</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="pm-card p-4" data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon size={14} className="text-emerald-700" />
                    <div className="font-display font-bold text-sm tracking-tight">{f.title}</div>
                  </div>
                  <div className="text-xs text-slate-500 leading-relaxed">{f.body}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* INTEGRATIONS (MOCKED) */}
        <section data-testid="integrations-section">
          <SectionTitle>Integrations · all <span className="pm-pill pm-pill-amber text-[10px] ml-2">MOCKED</span></SectionTitle>
          <div className="pm-card p-4 flex flex-wrap gap-2">
            {INTEGRATIONS.map((i) => (
              <span key={i} className="px-2.5 h-8 inline-flex items-center rounded-lg border border-slate-200 bg-white text-xs text-slate-700 font-semibold" data-testid={`integration-${i.replace(/[\s/().]+/g, "-").toLowerCase()}`}>
                {i}
              </span>
            ))}
          </div>
        </section>

        {/* SAVED TRIPS */}
        {savedTrips.length > 0 && (
          <section data-testid="saved-trips-section">
            <SectionTitle>Saved itineraries</SectionTitle>
            <div className="pm-card divide-y divide-slate-100">
              {savedTrips.slice(0, 5).map((t) => (
                <div key={t.id} className="p-4 flex items-center gap-4" data-testid={`saved-trip-${t.id}`}>
                  <div className="font-display font-bold flex-1 truncate">{t.name}</div>
                  <div className="text-xs text-slate-500 font-mono-tab">{t.start_date} → {t.end_date}</div>
                  <div className="text-xs text-slate-500">{t.matches_count} matches · {t.clients_covered} clients</div>
                  <div className="font-mono-tab text-sm font-bold">{fmtMoney(t.total_cost_eur)}</div>
                  <span className="pm-pill pm-pill-green text-[10px]">{t.status}</span>
                </div>
              ))}
            </div>
          </section>
        )}
          </div>
        )}
      </div>

      {/* Home Base modal — gates planFromSelection on first use */}
      <HomeBaseModal
        open={homeBaseModalOpen}
        initialCity={homeBase?.home_city}
        onConfirm={onHomeBaseConfirmed}
        onClose={() => setHomeBaseModalOpen(false)}
      />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 z-50" data-testid="travel-toast">
          <CheckCircle2 size={14} className="text-emerald-400" />
          <span className="text-sm font-bold">{toast}</span>
        </div>
      )}

      {/* TV Reminder Dialog (P8 spec: notification timing + calendar destination) */}
      {reminderFx && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          data-testid="tv-reminder-dialog"
          onClick={(e) => { if (e.target === e.currentTarget) setReminderFx(null); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center">
                <Tv size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Watch on TV</div>
                <div className="font-display text-lg font-bold tracking-tight truncate">{reminderFx.home_team} vs {reminderFx.away_team}</div>
                <div className="text-[11px] text-slate-500 font-mono-tab">{new Date(reminderFx.kickoff).toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <button onClick={() => setReminderFx(null)} className="text-slate-400 hover:text-slate-900 p-1" data-testid="tv-reminder-close"><ChevronRight size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Notify me</div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { v: "1w", label: "1 week before" },
                    { v: "24h", label: "24 hours before" },
                    { v: "3h", label: "3 hours before" },
                    { v: "kickoff", label: "At kick-off" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => setReminderTiming(opt.v)}
                      data-testid={`tv-reminder-timing-${opt.v}`}
                      className={`h-10 rounded-lg text-xs font-bold border-2 transition-all ${reminderTiming === opt.v ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Push to calendar</div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: "google", label: "Google" },
                    { v: "outlook", label: "Outlook" },
                    { v: "none", label: "No calendar" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => setReminderCal(opt.v)}
                      data-testid={`tv-reminder-cal-${opt.v}`}
                      className={`h-10 rounded-lg text-xs font-bold border-2 transition-all ${reminderCal === opt.v ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-[11px] text-slate-500 leading-relaxed">
                Mocked notification delivery — when Twilio + Google/Outlook are wired, this will push a real reminder.
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-200 flex items-center gap-2 bg-slate-50">
              <button onClick={() => setReminderFx(null)} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold" data-testid="tv-reminder-cancel">Cancel</button>
              <div className="flex-1" />
              <button onClick={confirmTvReminder} className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-1.5" data-testid="tv-reminder-confirm">
                <BellRing size={12} /> Set reminder
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* -------------------- shared -------------------- */
function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative inline-flex items-center gap-2 px-4 h-11 text-sm font-bold transition-colors ${
        active ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
      }`}
    >
      <Icon size={14} />
      {label}
      {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-emerald-500 rounded-full" />}
    </button>
  );
}

const KPI_TONES = {
  slate: { ring: "border-slate-200", value: "text-slate-900", chip: "bg-slate-100 text-slate-600" },
  blue:  { ring: "border-blue-200/70", value: "text-blue-700", chip: "bg-blue-50 text-blue-700" },
  red:   { ring: "border-red-200/70", value: "text-red-700", chip: "bg-red-50 text-red-700" },
  amber: { ring: "border-amber-200/70", value: "text-amber-700", chip: "bg-amber-50 text-amber-700" },
};

function KpiCard({ testid, label, value, sub, tone = "slate" }) {
  const t = KPI_TONES[tone] || KPI_TONES.slate;
  return (
    <div className={`pm-card p-4 border ${t.ring}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${t.chip} px-2 py-1 rounded inline-block`}>{label}</div>
      <div className={`font-display text-4xl font-black tracking-tight mt-2 ${t.value}`}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function shortName(full) {
  if (!full) return "";
  const parts = full.split(" ");
  if (parts.length <= 1) return full;
  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <h2 className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-2 flex-wrap">
        {children}
      </h2>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 px-4 py-3">{children}</th>;
}
function Td({ children }) {
  return <td className="px-4 py-3 align-middle">{children}</td>;
}

function Legend() {
  return (
    <div className="flex items-center gap-3 ml-3 flex-wrap">
      {["must_visit", "high", "recommended", "monitor"].map((k) => {
        const b = BADGE_META[k];
        return (
          <span key={k} className="inline-flex items-center gap-1.5 text-[10px] text-slate-700 font-bold">
            <span className="w-3.5 h-3.5 rounded-md ring-1 ring-white/40 shadow-sm" style={{ background: b.color }} />
            {b.label}
          </span>
        );
      })}
      <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-700 font-bold">
        <span className="w-3.5 h-3.5 rounded-md ring-1 ring-white/40 shadow-sm" style={{ background: "#7c3aed" }} />
        TV reminder set
      </span>
    </div>
  );
}

/* -------------------- trip card -------------------- */
function TripCard({ trip: initialTrip, onSave, onCalendarSync, busy, activeIndex }) {
  const [trip, setTrip] = useState(initialTrip);
  const [enriching, setEnriching] = useState(false);
  const [enrichedAt, setEnrichedAt] = useState(null);

  useEffect(() => { setTrip(initialTrip); setEnrichedAt(null); }, [initialTrip]);

  const findRealPrices = async () => {
    setEnriching(true);
    try {
      const { data } = await api.post("/travel/enrich/trip", trip);
      setTrip(data);
      setEnrichedAt(data.enriched_at);
    } finally {
      setEnriching(false);
    }
  };

  const trackBooking = async (provider, stopType, value) => {
    try {
      await api.post("/travel/affiliate-click", {
        provider, stop_type: stopType, trip_id: trip.id,
        destination: trip.name, dates: `${trip.start_date}..${trip.end_date}`,
        estimated_value_eur: value || 0,
      });
    } catch { /* never block the click */ }
  };

  return (
    <div className="pm-card overflow-hidden" data-testid={`trip-card-${activeIndex + 1}`}>
      <div className="p-5 flex items-start gap-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50/40 to-transparent">
        <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center font-display font-black">
          {activeIndex + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-2xl font-medium tracking-tight" data-testid="trip-title">{trip.name} · {trip.duration_days} days</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {fmtDate(trip.start_date)} — {fmtDate(trip.end_date)} · {trip.clients_covered} clients visited · {trip.matches_count} matches
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-4xl font-black text-emerald-700 leading-none flex items-end justify-end gap-1" data-testid="trip-ai-score">
            <Sparkles size={14} className="mb-1.5 text-emerald-600" /> {trip.ai_score}
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">AI score</div>
        </div>
      </div>

      <div className="px-5 py-4 space-y-2" data-testid="trip-stops">
        {trip.stops.map((s, i) => <StopRow key={i} stop={s} onBook={trackBooking} />)}
      </div>

      <div className="px-5 py-4 border-t border-slate-100 flex flex-wrap items-center gap-2 bg-slate-50/50">
        <Pill tone="green">{trip.clients_covered} clients covered</Pill>
        <Pill tone="blue">{trip.matches_count} matches</Pill>
        <Pill tone="amber">Est. {fmtMoney(trip.total_cost_eur)} flights+hotel</Pill>
        <Pill tone="violet">{trip.countries_count} countries</Pill>
        {enrichedAt && (
          <span className="pm-pill pm-pill-green text-[10px]" data-testid={`trip-enriched-${activeIndex + 1}`}>
            <Check size={10} className="inline mr-1" /> Real prices loaded
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {!enrichedAt && (
            <button
              className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5"
              onClick={findRealPrices}
              disabled={busy || enriching}
              data-testid={`find-real-prices-${activeIndex + 1}`}
            >
              <Sparkles size={13} /> {enriching ? "Finding…" : "Find real prices"}
            </button>
          )}
          <button
            className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5"
            onClick={onSave}
            disabled={busy}
            data-testid="save-itinerary-btn"
          ><Save size={13} /> Save itinerary</button>
          <button
            className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5"
            onClick={onCalendarSync}
            disabled={busy}
            data-testid="add-to-calendar-btn"
          ><CalendarIcon size={13} /> Add to calendar</button>
        </div>
      </div>
    </div>
  );
}

function StopRow({ stop, onBook }) {
  const Icon = STOP_ICON[stop.type] || ArrowRight;
  const enr = stop.enrichment;
  if (stop.type === "travel") {
    // Enriched: real flight options or rail-recommended swap
    if (enr?.kind === "train") {
      return (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-emerald-300 bg-emerald-50/60" data-testid={`stop-travel-${stop.from_city}-${stop.to_city}`}>
          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700">
            <Train size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm flex items-center gap-1.5 truncate">
              {stop.from_city} <ArrowRight size={12} className="text-slate-400" /> {stop.to_city}
              <span className="text-emerald-700 text-xs font-bold ml-2">{enr.train_name} · {Math.floor(enr.duration_min/60)}h{enr.duration_min%60}m · {fmtMoney(enr.price_eur)}</span>
              <span className="pm-pill pm-pill-green text-[10px] ml-1">Rail recommended</span>
            </div>
            <div className="text-[11px] text-emerald-700">{enr.reason}</div>
          </div>
          <a
            href={enr.book_url} target="_blank" rel="noopener"
            onClick={() => onBook && onBook(enr.provider || "omio", "train", enr.price_eur)}
            className="pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1"
            data-testid={`book-train-${stop.from_city}-${stop.to_city}`}
          >Book on Omio <ExternalLink size={11} /></a>
        </div>
      );
    }
    if (enr?.kind === "flight" && enr.cheapest) {
      const cheapest = enr.cheapest;
      return (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50/30" data-testid={`stop-travel-${stop.from_city}-${stop.to_city}`}>
          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-700">
            <Plane size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm flex items-center gap-1.5 truncate">
              {stop.from_city} <ArrowRight size={12} className="text-slate-400" /> {stop.to_city}
              <span className="text-indigo-700 text-xs font-bold ml-2">{cheapest.airline} · {cheapest.duration_min}min · {fmtMoney(cheapest.price_eur)}</span>
              {cheapest.tags?.includes("cheapest") && <span className="pm-pill pm-pill-green text-[10px] ml-1">Cheapest</span>}
              {cheapest.tags?.includes("stub") && <span className="pm-pill pm-pill-amber text-[10px] ml-1">Stub</span>}
            </div>
            <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
              {enr.options?.length > 1 && <span>+ {enr.options.length - 1} alternative</span>}
              <a
                href={`https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(stop.to_city + " city centre")}`}
                target="_blank" rel="noopener"
                onClick={() => onBook && onBook("uber", "transfer", 0)}
                className="text-slate-600 hover:text-slate-900 font-bold inline-flex items-center gap-1"
                data-testid={`transfer-uber-${stop.to_city}`}
              >Uber from arrival <ExternalLink size={9} /></a>
              <a
                href={`https://bolt.eu/?dropoff[address]=${encodeURIComponent(stop.to_city + " city centre")}`}
                target="_blank" rel="noopener"
                onClick={() => onBook && onBook("bolt", "transfer", 0)}
                className="text-slate-600 hover:text-slate-900 font-bold inline-flex items-center gap-1"
                data-testid={`transfer-bolt-${stop.to_city}`}
              >· Bolt <ExternalLink size={9} /></a>
            </div>
          </div>
          <a
            href={cheapest.book_url} target="_blank" rel="noopener"
            onClick={() => onBook && onBook("skyscanner", "flight", cheapest.price_eur)}
            className="pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold inline-flex items-center gap-1"
            data-testid={`book-flight-${stop.from_city}-${stop.to_city}`}
          >Book on Skyscanner <ExternalLink size={11} /></a>
        </div>
      );
    }
    // Default (no enrichment yet) — keep existing mocked render
    const carrierIcon = stop.carrier === "train" ? Train : Plane;
    const CarrierIcon = carrierIcon;
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-colors" data-testid={`stop-travel-${stop.from_city}-${stop.to_city}`}>
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700">
          <CarrierIcon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm flex items-center gap-1.5 truncate">
            {stop.from_city} <ArrowRight size={12} className="text-slate-400" /> {stop.to_city}
            <span className="text-slate-400 text-xs font-normal ml-2">{stop.airline_or_train} · {stop.class} · {fmtMoney(stop.price_eur)}</span>
          </div>
          {stop.alternative && (
            <div className="text-[11px] text-slate-500">or {stop.alternative.airline} · {fmtMoney(stop.alternative.price_eur)}</div>
          )}
        </div>
        <div className="text-right font-mono-tab text-xs text-slate-500 shrink-0">
          <div>{stop.date}</div>
          <div className="flex items-center gap-1 justify-end mt-0.5">
            <span className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">dep</span>
            <span>{stop.depart_time}</span>
            <ArrowRight size={9} className="text-slate-300" />
            <span className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">arr</span>
            <span>{stop.arrive_time}</span>
          </div>
        </div>
      </div>
    );
  }
  if (stop.type === "hotel") {
    if (enr?.kind === "hotel" && enr.options?.length > 0) {
      return (
        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-2" data-testid={`stop-hotel-${stop.city}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center text-violet-700">
              <HotelIcon size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">Overnight — {stop.city} · {stop.date}</div>
              <div className="text-[11px] text-slate-500">{enr.options.length} hotels found · prices from Booking.com</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pl-11">
            {enr.options.slice(0, 3).map((h, i) => (
              <a
                key={i} href={h.book_url} target="_blank" rel="noopener"
                onClick={() => onBook && onBook("booking_com", "hotel", h.price_eur)}
                className="block bg-white border border-violet-200 hover:border-violet-400 rounded-lg p-2 transition-colors"
                data-testid={`book-hotel-${stop.city}-${i}`}
              >
                <div className="font-bold text-xs truncate">{h.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{"★".repeat(h.stars || 3)} · {h.review_score?.toFixed(1)}/10{h.free_cancellation ? " · Free cancel" : ""}</div>
                <div className="font-bold text-sm text-violet-700 mt-1">{fmtMoney(h.price_eur)}/night</div>
              </a>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-violet-200 bg-violet-50/40" data-testid={`stop-hotel-${stop.city}`}>
        <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center text-violet-700">
          <HotelIcon size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate">{stop.name} <span className="text-xs text-slate-500 font-normal">★{stop.rating} · {stop.address}</span></div>
          <div className="text-[11px] text-slate-500">{stop.city}</div>
        </div>
        <div className="text-right font-mono-tab text-xs text-slate-500 shrink-0">
          <div>{stop.date}</div>
          <div className="font-bold text-slate-900">{fmtMoney(stop.price_eur)}</div>
        </div>
      </div>
    );
  }
  // match
  const b = BADGE_META[stop.badge] || BADGE_META.recommended;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: b.color + "40", background: b.color + "10" }} data-testid={`stop-match-${stop.fixture_id}`}>
      <div
        className="w-9 h-9 rounded-lg bg-slate-100 shrink-0"
        style={{ backgroundImage: stop.client_photo ? `url(${stop.client_photo})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate">{stop.home_team} vs {stop.away_team} <span className="text-xs text-slate-500 font-normal">— {stop.competition}</span></div>
        <div className="text-[11px] text-slate-500 truncate flex items-center gap-1.5">
          <span className="font-bold text-slate-700">{stop.client_name}</span>
          <span>·</span>
          <span>{stop.stadium}</span>
          <span>·</span>
          <span className={`pm-pill text-[10px] ${b.tone}`}>{b.label}</span>
        </div>
        {stop.reasons?.length > 0 && (
          <div className="mt-1 text-[11px] text-slate-600 italic flex items-start gap-1" data-testid={`stop-reasoning-${stop.fixture_id}`}>
            <Sparkles size={10} className="text-emerald-600 mt-0.5 shrink-0" />
            <span className="truncate">{stop.reasons.slice(0, 2).join(" · ")}</span>
          </div>
        )}
        {stop.context_line && (
          <div className="mt-0.5 text-[11px] text-slate-500 flex items-start gap-1" data-testid={`stop-context-${stop.fixture_id}`}>
            <span className="w-2.5 shrink-0" />
            <span className="truncate">{stop.context_line}</span>
          </div>
        )}
      </div>
      <div className="text-right font-mono-tab text-xs text-slate-500 shrink-0">
        <div>{stop.date}</div>
        <div className="font-bold text-slate-900">{stop.kickoff.slice(11, 16)}</div>
      </div>
    </div>
  );
}

function Pill({ tone, children }) {
  const cls = {
    green:  "bg-emerald-50 text-emerald-800 border-emerald-200",
    blue:   "bg-blue-50 text-blue-800 border-blue-200",
    amber:  "bg-amber-50 text-amber-800 border-amber-200",
    violet: "bg-violet-50 text-violet-800 border-violet-200",
  }[tone] || "bg-slate-50 text-slate-800 border-slate-200";
  return <span className={`px-2.5 h-7 inline-flex items-center rounded-lg border text-[11px] font-bold ${cls}`}>{children}</span>;
}
